import { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Slider } from './ui/slider';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import {
  Sun, Clock, ArrowDown, RotateCcw, Play, Pause,
  Ruler, Building, User, Mountain
} from 'lucide-react';
import { useI18n } from '../context/I18nContext';

const ShadowCalculator = ({ latitude, longitude, sunTimes }) => {
  const { t } = useI18n();
  const canvasRef = useRef(null);
  const [objectHeight, setObjectHeight] = useState(10); // meters
  const [timeOfDay, setTimeOfDay] = useState(12);
  const [isAnimating, setIsAnimating] = useState(false);
  const [presetObject, setPresetObject] = useState('custom');
  const animationRef = useRef(null);

  // Object presets
  const presets = {
    person: { height: 1.75, label: t('shadow.presetPerson'), icon: User },
    tree: { height: 8, label: t('shadow.presetTree'), icon: Mountain },
    building: { height: 15, label: t('shadow.presetBuilding'), icon: Building },
    tower: { height: 50, label: t('shadow.presetTower'), icon: Building },
    custom: { height: objectHeight, label: t('shadow.presetCustom'), icon: Ruler },
  };

  // Calculate sun position
  const calculateSunPosition = useCallback((hour) => {
    const dayOfYear = Math.floor((new Date() - new Date(new Date().getFullYear(), 0, 0)) / 86400000);
    const declination = 23.45 * Math.sin((360 / 365) * (dayOfYear - 81) * Math.PI / 180);

    const solarNoon = 12.0 - longitude / 15.0;
    const hourAngle = 15 * (hour - solarNoon);

    const latRad = latitude * Math.PI / 180;
    const decRad = declination * Math.PI / 180;
    const haRad = hourAngle * Math.PI / 180;

    const sinAlt = Math.sin(latRad) * Math.sin(decRad) +
                   Math.cos(latRad) * Math.cos(decRad) * Math.cos(haRad);
    const altitude = Math.asin(Math.max(-1, Math.min(1, sinAlt))) * 180 / Math.PI;

    const cosAz = (Math.sin(decRad) - Math.sin(latRad) * Math.sin(altitude * Math.PI / 180)) /
                  (Math.cos(latRad) * Math.cos(altitude * Math.PI / 180) + 0.0001);
    let azimuth = Math.acos(Math.max(-1, Math.min(1, cosAz))) * 180 / Math.PI;
    if (hourAngle > 0) azimuth = 360 - azimuth;

    return { azimuth, altitude };
  }, [latitude, longitude]);

  // Calculate shadow length
  const calculateShadowLength = (sunAltitude, height) => {
    if (sunAltitude <= 0) return Infinity;
    return height / Math.tan(sunAltitude * Math.PI / 180);
  };

  // Calculate shadow direction (opposite to sun azimuth)
  const calculateShadowDirection = (sunAzimuth) => {
    return (sunAzimuth + 180) % 360;
  };

  const sunPos = calculateSunPosition(timeOfDay);
  const shadowLength = calculateShadowLength(sunPos.altitude, objectHeight);
  const shadowDirection = calculateShadowDirection(sunPos.azimuth);

  // Draw visualization
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;

    // Clear
    ctx.fillStyle = '#1a1a2e';
    ctx.fillRect(0, 0, width, height);

    // Ground
    const groundY = height * 0.75;
    ctx.fillStyle = '#2a3a2a';
    ctx.fillRect(0, groundY, width, height - groundY);

    // Grid on ground
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.1)';
    ctx.lineWidth = 1;
    for (let i = 0; i < 10; i++) {
      const y = groundY + i * 10;
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(width, y);
      ctx.stroke();
    }

    // Sky gradient
    const skyGradient = ctx.createLinearGradient(0, 0, 0, groundY);
    if (sunPos.altitude > 6) {
      skyGradient.addColorStop(0, '#1e90ff');
      skyGradient.addColorStop(1, '#87ceeb');
    } else if (sunPos.altitude > 0) {
      skyGradient.addColorStop(0, '#4a6fa5');
      skyGradient.addColorStop(1, '#ff8c42');
    } else {
      skyGradient.addColorStop(0, '#0a0a2e');
      skyGradient.addColorStop(1, '#1a1a3e');
    }
    ctx.fillStyle = skyGradient;
    ctx.fillRect(0, 0, width, groundY);

    // Sun
    if (sunPos.altitude > -5) {
      const sunX = width * 0.3 + (sunPos.azimuth - 90) * 0.5;
      const sunY = groundY - 50 - sunPos.altitude * 2;

      const sunGradient = ctx.createRadialGradient(sunX, sunY, 0, sunX, sunY, 30);
      sunGradient.addColorStop(0, sunPos.altitude > 6 ? '#ffffd0' : '#ff8040');
      sunGradient.addColorStop(0.5, sunPos.altitude > 6 ? 'rgba(255, 240, 100, 0.5)' : 'rgba(255, 100, 50, 0.5)');
      sunGradient.addColorStop(1, 'rgba(255, 150, 50, 0)');
      ctx.fillStyle = sunGradient;
      ctx.beginPath();
      ctx.arc(sunX, sunY, 30, 0, Math.PI * 2);
      ctx.fill();
    }

    // Object
    const objX = width * 0.5;
    const objBaseY = groundY;
    const objScale = 3; // pixels per meter
    const objHeightPx = objectHeight * objScale;

    // Draw object (rectangle)
    ctx.fillStyle = '#4a5568';
    ctx.fillRect(objX - 10, objBaseY - objHeightPx, 20, objHeightPx);

    // Object outline
    ctx.strokeStyle = '#718096';
    ctx.lineWidth = 2;
    ctx.strokeRect(objX - 10, objBaseY - objHeightPx, 20, objHeightPx);

    // Height label
    ctx.fillStyle = 'white';
    ctx.font = '10px sans-serif';
    ctx.textAlign = 'left';
    ctx.fillText(`${objectHeight}m`, objX + 15, objBaseY - objHeightPx / 2);

    // Shadow
    if (sunPos.altitude > 0 && shadowLength < 1000) {
      const shadowLengthPx = Math.min(shadowLength * objScale, width * 0.8);
      const shadowDirRad = (shadowDirection - 90) * Math.PI / 180;

      // Shadow on ground (trapezoid)
      ctx.fillStyle = 'rgba(0, 0, 0, 0.4)';
      ctx.beginPath();
      ctx.moveTo(objX - 10, objBaseY);
      ctx.lineTo(objX + 10, objBaseY);
      ctx.lineTo(objX + shadowLengthPx * 0.3, objBaseY + shadowLengthPx * 0.3);
      ctx.lineTo(objX - shadowLengthPx * 0.1, objBaseY + shadowLengthPx * 0.3);
      ctx.closePath();
      ctx.fill();

      // Shadow length label
      ctx.fillStyle = '#f59e0b';
      ctx.font = 'bold 12px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(`${shadowLength.toFixed(1)}m`, objX + shadowLengthPx * 0.15, objBaseY + shadowLengthPx * 0.15 + 15);

      // Direction arrow
      ctx.strokeStyle = '#f59e0b';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(objX, objBaseY + 5);
      ctx.lineTo(objX + shadowLengthPx * 0.2, objBaseY + shadowLengthPx * 0.2);
      ctx.stroke();
    } else if (sunPos.altitude <= 0) {
      ctx.fillStyle = '#6b7280';
      ctx.font = '14px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(t('shadow.noShadowSunBelowHorizon'), width / 2, groundY + 40);
    }

    // Compass rose
    const compassX = width - 50;
    const compassY = 50;
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.5)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.arc(compassX, compassY, 30, 0, Math.PI * 2);
    ctx.stroke();

    // Shadow direction indicator
    const shadowDirRad = (shadowDirection - 90) * Math.PI / 180;
    ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';
    ctx.beginPath();
    ctx.moveTo(compassX, compassY);
    ctx.lineTo(compassX + Math.cos(shadowDirRad) * 25, compassY + Math.sin(shadowDirRad) * 25);
    ctx.lineTo(compassX + Math.cos(shadowDirRad + 0.3) * 15, compassY + Math.sin(shadowDirRad + 0.3) * 15);
    ctx.lineTo(compassX + Math.cos(shadowDirRad - 0.3) * 15, compassY + Math.sin(shadowDirRad - 0.3) * 15);
    ctx.closePath();
    ctx.fill();

    // Sun direction indicator
    const sunDirRad = (sunPos.azimuth - 90) * Math.PI / 180;
    ctx.fillStyle = '#f59e0b';
    ctx.beginPath();
    ctx.moveTo(compassX, compassY);
    ctx.lineTo(compassX + Math.cos(sunDirRad) * 25, compassY + Math.sin(sunDirRad) * 25);
    ctx.lineTo(compassX + Math.cos(sunDirRad + 0.3) * 15, compassY + Math.sin(sunDirRad + 0.3) * 15);
    ctx.lineTo(compassX + Math.cos(sunDirRad - 0.3) * 15, compassY + Math.sin(sunDirRad - 0.3) * 15);
    ctx.closePath();
    ctx.fill();

    ctx.fillStyle = 'white';
    ctx.font = 'bold 10px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('N', compassX, compassY - 35);
    ctx.fillText('S', compassX, compassY + 42);
    ctx.fillText('E', compassX + 38, compassY + 4);
    ctx.fillText('W', compassX - 38, compassY + 4);

    // Info overlay
    ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
    ctx.fillRect(10, 10, 140, 55);
    ctx.fillStyle = 'white';
    ctx.font = '11px monospace';
    ctx.textAlign = 'left';
    ctx.fillText(`${t('shadow.time')}: ${Math.floor(timeOfDay)}:${((timeOfDay % 1) * 60).toFixed(0).padStart(2, '0')}`, 18, 28);
    ctx.fillText(`${t('shadow.sunAlt')}: ${sunPos.altitude.toFixed(1)}°`, 18, 42);
    ctx.fillText(`${t('shadow.sunAz')}: ${sunPos.azimuth.toFixed(0)}°`, 18, 56);

  }, [objectHeight, timeOfDay, sunPos, shadowLength, shadowDirection, t]);

  // Animation
  useEffect(() => {
    if (isAnimating) {
      animationRef.current = setInterval(() => {
        setTimeOfDay(prev => {
          const next = prev + 0.1;
          return next > 20 ? 6 : next;
        });
      }, 100);
    } else {
      if (animationRef.current) {
        clearInterval(animationRef.current);
      }
    }
    return () => {
      if (animationRef.current) {
        clearInterval(animationRef.current);
      }
    };
  }, [isAnimating]);

  const getDirectionName = (degrees) => {
    const directions = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
    const index = Math.round(degrees / 45) % 8;
    return directions[index];
  };

  return (
    <div className="space-y-4">
      {/* Visualization */}
      <Card className="bg-slate-900/50 border-white/10">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-heading flex items-center gap-2">
              <ArrowDown className="w-4 h-4 text-amber-400" />
              {t('shadow.title')}
            </CardTitle>
            {sunPos.altitude > 0 ? (
              <Badge className="bg-amber-500/20 text-amber-300">
                {t('shadow.shadowLabel')}: {shadowLength < 1000 ? `${shadowLength.toFixed(1)}m` : '∞'}
              </Badge>
            ) : (
              <Badge className="bg-slate-500/20 text-slate-400">
                {t('shadow.noShadow')}
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Canvas */}
          <div className="relative rounded-lg overflow-hidden border border-white/10">
            <canvas
              ref={canvasRef}
              width={400}
              height={220}
              className="w-full"
            />
          </div>

          {/* Object Presets */}
          <div className="flex flex-wrap gap-2">
            {Object.entries(presets).map(([key, preset]) => {
              const Icon = preset.icon;
              return (
                <Button
                  key={key}
                  variant={presetObject === key ? 'default' : 'outline'}
                  size="sm"
                  className={`h-8 ${presetObject === key ? 'bg-amber-500 text-black' : 'border-white/10'}`}
                  onClick={() => {
                    setPresetObject(key);
                    if (key !== 'custom') {
                      setObjectHeight(preset.height);
                    }
                  }}
                >
                  <Icon className="w-3 h-3 mr-1" />
                  {preset.label}
                </Button>
              );
            })}
          </div>

          {/* Controls */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-xs text-slate-400 flex items-center gap-1">
                <Ruler className="w-3 h-3" />
                {t('shadow.objectHeight')}: {objectHeight}m
              </Label>
              <Slider
                value={[objectHeight]}
                onValueChange={([v]) => {
                  setObjectHeight(v);
                  setPresetObject('custom');
                }}
                min={0.5}
                max={100}
                step={0.5}
              />
            </div>

            <div className="space-y-2">
              <Label className="text-xs text-slate-400 flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {t('shadow.time')}: {Math.floor(timeOfDay)}:{((timeOfDay % 1) * 60).toFixed(0).padStart(2, '0')}
              </Label>
              <Slider
                value={[timeOfDay]}
                onValueChange={([v]) => setTimeOfDay(v)}
                min={0}
                max={24}
                step={0.25}
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsAnimating(!isAnimating)}
              className="flex-1 border-white/10"
            >
              {isAnimating ? <Pause className="w-4 h-4 mr-1" /> : <Play className="w-4 h-4 mr-1" />}
              {isAnimating ? t('shadow.pause') : t('shadow.animateDay')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setObjectHeight(10);
                setTimeOfDay(12);
                setPresetObject('custom');
              }}
              className="border-white/10"
            >
              <RotateCcw className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      <Card className="bg-slate-900/50 border-white/10">
        <CardContent className="p-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-slate-800/50 rounded p-3">
              <p className="text-xs text-slate-500 mb-1">{t('shadow.shadowLength')}</p>
              <p className="font-mono text-xl font-bold text-amber-400">
                {sunPos.altitude > 0 ? (shadowLength < 1000 ? `${shadowLength.toFixed(1)}m` : '∞') : '-'}
              </p>
              <p className="text-xs text-slate-500 mt-1">
                {sunPos.altitude > 0 ? `${(shadowLength / objectHeight).toFixed(1)}${t('shadow.timesObjectHeight')}` : t('shadow.sunBelowHorizon')}
              </p>
            </div>
            <div className="bg-slate-800/50 rounded p-3">
              <p className="text-xs text-slate-500 mb-1">{t('shadow.shadowDirection')}</p>
              <p className="font-mono text-xl font-bold text-white">
                {sunPos.altitude > 0 ? `${shadowDirection.toFixed(0)}° ${getDirectionName(shadowDirection)}` : '-'}
              </p>
              <p className="text-xs text-slate-500 mt-1">
                {t('shadow.oppositeToSun')}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Formula */}
      <Card className="bg-slate-900/50 border-white/10">
        <CardContent className="p-3 text-xs text-slate-400">
          <p className="mb-2">📐 <strong>{t('shadow.formulaLabel')}</strong> {t('shadow.formulaText')}</p>
          <p>☀️ <strong>{t('shadow.currentSunLabel')}</strong> {t('shadow.alt')} {sunPos.altitude.toFixed(1)}° / {t('shadow.az')} {sunPos.azimuth.toFixed(0)}°</p>
        </CardContent>
      </Card>
    </div>
  );
};

export default ShadowCalculator;
